// App-wide "current context" event bus. Plain pub/sub (no React dependency) so
// any part of the app can emit a change and any part (e.g. the AI Assistant)
// can subscribe, regardless of where each sits in the component tree.

export type ViewType =
  | 'dashboard'
  | 'onboarding'
  | 'project'
  | 'validation'
  | 'document'
  | 'document-entities'
  | 'document-presentation'
  | 'document-editor'
  | 'items'
  | 'item-detail'
  | 'item-details'
  | 'tenants'
  | 'users'
  | 'tags'
  | 'login'
  | 'register'
  | 'request-access'
  | 'unknown';

export interface ViewContext {
  view: ViewType;
  projectId: number | null;
  projectName: string | null;
  documentId: number | null;
  documentName: string | null;
  documentSectionId: number | null;
  documentSectionTitle: string | null;
  /** True when the selected section has no parent section (top-level). */
  documentSectionIsRoot: boolean | null;
  documentEntityId: number | null;
  documentEntityIdentifier: string | null;
  documentEntityName: string | null;
}

const EMPTY_CONTEXT: ViewContext = {
  view: 'unknown',
  projectId: null,
  projectName: null,
  documentId: null,
  documentName: null,
  documentSectionId: null,
  documentSectionTitle: null,
  documentSectionIsRoot: null,
  documentEntityId: null,
  documentEntityIdentifier: null,
  documentEntityName: null,
};

type Listener = (context: ViewContext) => void;

let context: ViewContext = { ...EMPTY_CONTEXT };
const listeners = new Set<Listener>();

function notify(): void {
  listeners.forEach((listener) => listener(context));
}

/** Current snapshot — for reads that don't need a live subscription. */
export function getViewContext(): ViewContext {
  return context;
}

/** Subscribes to every context change; call the returned function to unsubscribe. */
export function subscribeToViewContext(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Replaces the whole context for a view change — navigating to a new view
 * implicitly clears whatever document/section/entity was selected in the
 * previous one.
 */
export function setViewContext(patch: Partial<ViewContext> & { view: ViewType }): void {
  context = { ...EMPTY_CONTEXT, ...patch };
  notify();
}

/**
 * Merges into the current context without touching `view` — for enriching the
 * active view once async data resolves (e.g. a document's name), and, once
 * supported, for section/entity selection within the current view.
 */
export function updateViewContext(patch: Partial<Omit<ViewContext, 'view'>>): void {
  context = { ...context, ...patch };
  notify();
}
